import { useCallback, useRef, useState } from 'react';
import { useContext } from 'react';

import { useSelector } from 'react-redux';

import FileSaver from 'file-saver';
import { db } from 'firebase/firebase';
import { deleteDoc, doc } from 'firebase/firestore';
import html2canvas from 'html2canvas';
import { useCurrentPng } from 'recharts-to-png';

import { SlidePanelContext } from 'packages/dashboard/components/SlidePanel/SlidePanelContext';

import { DASHBOARDS_FORMS } from '../../../constants';

/**
 * Hook to handle widget actions: edit, delete, and download
 */
export const useWidgetActions = ({ widget, config, onEdit }) => {
  const { collectionName, componentType } = config;
  const { id, title } = widget;
  const { boardSelected } = useContext(SlidePanelContext);
  const orgId = useSelector(state => state.organizations.organization.id);
  const [getComposedPng, { ref: chartRef, isLoading: downloadingPngChart }] = useCurrentPng();
  const counterRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // Use chartRef for charts, counterRef for counter
  const ref =
    componentType === DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY ? counterRef : chartRef;

  // Unified downloading state: combines PNG chart loading and manual downloading state
  const isDownloading = downloading || downloadingPngChart;

  const handleDownload = useCallback(async () => {
    if (!ref.current) {
      console.warn('Widget ref is not available for download');
      return;
    }

    setDownloading(true);

    // Use html2canvas for counter, useCurrentPng for charts
    if (componentType === DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY) {
      try {
        const canvas = await html2canvas(ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true,
        });

        canvas.toBlob(blob => {
          if (blob) {
            const noSpacesTitle = title.replaceAll(' ', '_');
            FileSaver.saveAs(blob, `${noSpacesTitle}.png`);
          }
          setDownloading(false);
        });
      } catch (error) {
        console.error('Error downloading counter:', error);
        setDownloading(false);
      }
    } else {
      try {
        const png = await getComposedPng();
        if (png) {
          const noSpacesTitle = title.replaceAll(' ', '_');
          FileSaver.saveAs(png, `${noSpacesTitle}.png`);
        }
        setDownloading(false);
      } catch (error) {
        console.error('Error downloading PNG:', error);
        setDownloading(false);
      }
    }
  }, [componentType, getComposedPng, title, ref]);

  const handleDelete = useCallback(() => {
    const collectionPath = boardSelected?.divId
      ? [
          'organizations',
          orgId,
          'divisions',
          boardSelected.divId,
          'dashboards',
          boardSelected.id,
          collectionName,
          id,
        ]
      : ['organizations', orgId, 'dashboards', boardSelected.id, collectionName, id];
    const docRef = doc(db, ...collectionPath);
    deleteDoc(docRef);
  }, [boardSelected, orgId, collectionName, id]);

  const handleEdit = useCallback(() => {
    // Table expects only the id (string), other components expect the full widget object
    if (componentType === DASHBOARDS_FORMS.EVENT_TABLE) {
      onEdit(widget.id, componentType);
    } else {
      onEdit(widget, componentType);
    }
  }, [componentType, widget, onEdit]);

  return {
    handleEdit,
    handleDelete,
    handleDownload,
    downloading: isDownloading,
    setDownloading,
    ref,
  };
};
